// Live's subtitles picker (ui-templates' `shared/subtitles-popover`, spec G14-templates PR 15): the
// footer chip ("On, Bottom" or "Off") and its popover: the switch, the place and the caption
// style, each style live's picture with its name on hover. The styles are the server's.
import { Image, Popover, Radio, Stack, Switch, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Captions } from "lucide-react";
import { useState } from "react";

import type { TemplateCaptionStyle, VideoCaptionPosition } from "../../../api/generated/model";
import boldCinematic from "./caption-styles/bold-cinematic.png";
import bubble from "./caption-styles/bubble.png";
import classic from "./caption-styles/classic.png";
import glow from "./caption-styles/glow.png";
import karaoke from "./caption-styles/karaoke.png";
import label from "./caption-styles/label.png";
import outline from "./caption-styles/outline.png";
import classes from "./forms.module.css";

/** Live's pictures of its styles (`presets/assets/caption-*.png`), byte for byte, by style id. */
const PICTURES: Readonly<Record<string, string>> = {
  classic,
  bubble,
  label,
  glow,
  outline,
  "bold-cinematic": boldCinematic,
  karaoke,
};

/** Live's words (`subtitles-popover/constants.ts`). */
export const SUBTITLES_UNAVAILABLE = "No caption styles are published for this template yet.";
const POSITIONS: { value: VideoCaptionPosition; label: string }[] = [
  { value: "top", label: "Top" },
  { value: "center", label: "Center" },
  { value: "bottom", label: "Bottom" },
];

export interface SubtitlesValue {
  on: boolean;
  position: VideoCaptionPosition;
  /** A style id of the server's; "" until they are read. */
  styleId: string;
}

/** The chip's words: "On, Bottom", else "Off" (live's `toSubtitlesLabel`). */
export function subtitlesLabel(value: SubtitlesValue): string {
  const place = POSITIONS.find((each) => each.value === value.position)?.label ?? "Bottom";
  return value.on ? `On, ${place}` : "Off";
}

export interface SubtitlesProps {
  value: SubtitlesValue;
  /** The template's styles; none yet while they are read. */
  styles: readonly TemplateCaptionStyle[] | undefined;
  /** Live's tooltip on the chip, the form's own. */
  tip: string;
  disabled: boolean;
  onChange: (value: SubtitlesValue) => void;
}

/**
 * Live's chip and popover: "Subtitles" with its switch; "Position" (Top, Center, Bottom) and
 * "Caption style" (live's pictures, three a row, the picked one marked), both greyed while off.
 */
export function Subtitles({ value, styles, tip, disabled, onChange }: SubtitlesProps) {
  const [opened, setOpened] = useState(false);
  const set = (change: Partial<SubtitlesValue>) => onChange({ ...value, ...change });
  if (styles !== undefined && styles.length === 0) {
    // Live's chip with nothing to pick: greyed, its words on hover.
    return (
      <Tooltip label={SUBTITLES_UNAVAILABLE} multiline maw={280} withArrow>
        <span>
          <UnstyledButton className={classes.chip} aria-label={`Subtitles: ${subtitlesLabel(value)}`} disabled>
            <Captions size={16} strokeWidth={1.5} aria-hidden />
            <span>{subtitlesLabel(value)}</span>
          </UnstyledButton>
        </span>
      </Tooltip>
    );
  }
  return (
    // Live's `SettingPopover`: 357 px wide (its `DEFAULT_WIDTH`).
    <Popover opened={opened} onChange={setOpened} position="top-start" width={357} withinPortal={false}>
      <Popover.Target>
        <Tooltip label={tip} multiline maw={280} withArrow disabled={opened}>
          <UnstyledButton
            className={classes.chip}
            aria-label={`Subtitles: ${subtitlesLabel(value)}`}
            disabled={disabled}
            onClick={() => setOpened((open) => !open)}
          >
            <Captions size={16} strokeWidth={1.5} aria-hidden />
            <span>{subtitlesLabel(value)}</span>
          </UnstyledButton>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown className={classes.subtitles}>
        <Stack gap={12}>
          <Switch
            label="Subtitles"
            labelPosition="left"
            checked={value.on}
            onChange={(event) => set({ on: event.currentTarget.checked })}
          />
          <Text size="sm" {...(value.on ? {} : { c: "dimmed" })}>
            Position
          </Text>
          {/* Live's `RadioCard` of the same width each (`isOptionSameWidth`). */}
          <Radio.Group
            aria-label="Position"
            value={value.position}
            onChange={(next) => set({ position: next as VideoCaptionPosition })}
          >
            <div className={classes.pillsEven}>
              {POSITIONS.map((each) => (
                <Radio.Card key={each.value} value={each.value} className={classes.pill} disabled={!value.on}>
                  {each.label}
                </Radio.Card>
              ))}
            </div>
          </Radio.Group>
          <Text size="sm" {...(value.on ? {} : { c: "dimmed" })}>
            Caption style
          </Text>
          <div className={classes.styles}>
            {(styles ?? []).map((style) => (
              <Tooltip key={style.id} label={style.name} withArrow>
                <UnstyledButton
                  className={classes.style}
                  aria-label={style.name}
                  aria-pressed={style.id === value.styleId}
                  disabled={!value.on}
                  onClick={() => set({ styleId: style.id })}
                >
                  {PICTURES[style.id] === undefined ? (
                    <Text size="xs">{style.name}</Text>
                  ) : (
                    <Image src={PICTURES[style.id]} alt="" className={classes.stylePicture} />
                  )}
                </UnstyledButton>
              </Tooltip>
            ))}
          </div>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
